import {
 useEffect, useState, useCallback, useMemo, useRef,
} from 'react';
import {
 commitMutation, graphql, fetchQuery, useRelayEnvironment, useFragment,
} from 'react-relay';
import { nanoid } from 'nanoid';
import { isMobile } from 'react-device-detect';
import Icon from '@/core/icon';
import { lang, useTranslation } from '@/helpers/translate';
import Formules from '@/components/space/public/cartridge/formules';
import Riders from '@/components/space/public/cartridge/riders';
import Resume from '@/components/space/public/cartridge/resume';
import { queryCalendar } from '@/components/space/public/byCategory/calendar';
import { useSpacePublicZustandStore } from '@/helpers/spacepublicprovider';
import { useZusUserConnectedStore } from '@/helpers/user-auth.js';
import get from '@/helpers/getValue';
import CartridgeDates from '@/components/space/public/cartridge/dates';
import { useZusHomeStore } from '@/helpers/context';
import Button from '@/core/button.js';
import Loading from '@/core/loading.js';
import { TrackerComponent } from '@/helpers/tracker';
import { UseZusModalStore, ModalWrapper } from '@/helpers/modal.js';
import { useZusTrackerStore } from '@/helpers/tracker.js';

// Constantes pour les valeurs répétées
const STEPS = {
  CHECKING_LAST_BOOKING: 'checkingLastBooking',
  STEP1: 'step1',
  STEP2: 'step2',
  NEED_LOGIN_SIGNUP: 'needLoginSignup',
  NEED_LOGIN_SIGNUP_NO_CATEGORY: 'needLoginSignupnoCategory',
};

// GraphQL mutations et queries
const cartridgeCreateBookingMutation = graphql`
  mutation cartridgeCreateBookingMutation(
    $spaceId: String
    $category: String
    $riders: Int
    $dates: JSONObject
    $riderRef: String
    $lang: String
  ) {
    createNewBooking(
      spaceId: $spaceId
      category: $category
      riders: $riders
      dates: $dates
      riderRef: $riderRef
    ) {
      frontURL(lang: $lang)
    }
  }
`;

const cartridgeQueryLastBookingQuery = graphql`
  query cartridgeQueryLastBookingQuery(
    $spaceRef: String!
    $userRef: String!
    $lang: String
  ) {
    space: getSpaceByRef(ref: $spaceRef) {
      ref
      lastBookingByUser(userRef: $userRef) {
        ref
        frontURL(lang: $lang)
      }
    }
  }
`;

// Hook personnalisé pour la création de réservation
const useCreateBooking = (environment, spaceId, spaceRef, datesSelected, category, userData, isAuthenticated, langCurrent) => {
  const { t } = useTranslation();
  const { setModal1 } = UseZusModalStore.getState();
  const { setOrEvent } = useZusTrackerStore.getState();
  const userAuthOpenModal = useZusUserConnectedStore((state) => state.userAuthOpenModal);
  const riders = useSpacePublicZustandStore((state) => state.riders);

  const gotoBookingUrl = useCallback((url) => {
    window.location.href = url;
  }, []);

  const onClickCreation = useCallback((variables, callback) => {
    commitMutation(environment, {
      mutation: cartridgeCreateBookingMutation,
      variables,
      onCompleted: (res, err) => {
        callback(null, err?.[0]?.message || res);
      },
      onError: (err) => {
        console.error(err);
        callback(true);
      },
    });
  }, [environment]);

  const createBooking = useCallback((options = {}) => new Promise((resolved) => {
    const data = {
      riders,
      spaceId,
      category: get(options, 'noCategory') ? null : category,
      dates: Object.entries(datesSelected || {}).length
        ? datesSelected
        : null,
      riderRef: userData ? get(userData, 'ref') : null,
      lang: langCurrent,
    };

    if (!isAuthenticated) {
      userAuthOpenModal('signup');
      resolved({
        success: true,
        step: get(options, 'noCategory') ? STEPS.NEED_LOGIN_SIGNUP_NO_CATEGORY : STEPS.NEED_LOGIN_SIGNUP,
      });
      return;
    }

    if (isAuthenticated && !userData?.corpo?.ref) {
      userAuthOpenModal('corpostep');
      resolved({
        success: true,
        step: get(options, 'noCategory') ? STEPS.NEED_LOGIN_SIGNUP_NO_CATEGORY : STEPS.NEED_LOGIN_SIGNUP,
      });
      return;
    }

    onClickCreation(data, (err, res) => {
      if (err || !res?.createNewBooking?.frontURL) {
        if (res === 'error.space.datenotavailable') {
          setModal1(
            <ModalWrapper onClose={() => setModal1(null)}>
              <div className="flex-col p-12 bg-white rounded-2xl text-purple-5/50 md:flex-row">
                {t('error:space.datenotavailable')}
              </div>
            </ModalWrapper>,
          );
        } else if (res === 'partnercantafford') {
          setModal1(
            <ModalWrapper onClose={() => setModal1(null)}>
              <div className="flex-col p-12 bg-white rounded-2xl text-purple-5/50 md:flex-row">
                {t('error:formule.partnercantafford')}
              </div>
            </ModalWrapper>,
          );
        }
        resolved({ success: false });
        return;
      }

      const newBookingURL = process.env.NEXT_PUBLIC_URL_V3 + (res?.createNewBooking?.frontURL || '');

      try {
        const lead = {
          spaceId,
          spaceRef,
          riders,
          category: get(options, 'noCategory') ? null : category,
          dates: Object.entries(datesSelected || {}).length ? JSON.stringify(datesSelected) : null,
        };
        setOrEvent('lead', null, lead);
      } catch (e) {
        console.log('error trackerNewBooking', e);
      }

      setTimeout(() => {
        if (useZusUserConnectedStore.getState().what === 'companyconfirm') {
          useZusUserConnectedStore.setState({ newBookingURL });
        } else {
          gotoBookingUrl(newBookingURL);
        }
      }, 150);

      resolved({ success: true });
    });
  }), [environment, spaceId, spaceRef, datesSelected, category, userData, isAuthenticated, langCurrent, riders, t, setModal1, setOrEvent, userAuthOpenModal, onClickCreation, gotoBookingUrl]);

  return { createBooking, gotoBookingUrl };
};

// Hook personnalisé pour la gestion des étapes
const useStepManagement = (isAuthenticated, userData, spaceRef, environment, langCurrent) => {
  const [step, setStep] = useState(
    isAuthenticated ? { value: STEPS.CHECKING_LAST_BOOKING } : { value: STEPS.STEP2 },
  );

  useEffect(() => {
    if (
      step?.value?.match(/^needLoginSignup/)
      && isAuthenticated
      && userData?.ref
      && userData?.corpo?.ref
    ) {
      if (userData?.signup.step2 && userData?.corpo?.ref) {
        setStep({ value: STEPS.STEP2 });
      }
    } else if (isAuthenticated && userData && userData.ref && userData?.corpo?.ref) {
      setStep({ value: STEPS.CHECKING_LAST_BOOKING });
      fetchQuery(environment, cartridgeQueryLastBookingQuery, {
        spaceRef,
        userRef: userData.ref,
        lang: langCurrent,
      }).subscribe({
        next: (spaceData) => {
          if (get(spaceData, 'space.lastBookingByUser.frontURL')) {
            setStep({ value: STEPS.STEP1, spaceData });
          } else {
            setStep({ value: STEPS.STEP2 });
          }
        },
        error: () => {
          setStep({ value: STEPS.STEP2 });
        },
      });
    } else if (
      !isAuthenticated
      && step.value !== STEPS.STEP2
      && !step.value.match(/^needLoginSignup/)
    ) {
      setStep({ value: STEPS.STEP2 });
    }
  }, [isAuthenticated, userData, spaceRef, environment, langCurrent]);

  return { step, setStep };
};

// Hook personnalisé pour la gestion de l'affichage du bouton
const useButtonVisibility = (ua) => {
  const observerRef = useRef(null);
  const targetRef = useRef(null);

  const setupIntersectionObserver = useCallback(() => {
    if (!ua.isDesktop || !document.getElementById('spacepubliccartridgeonlybuttonifsiblingout')) return;

    // Nettoyer l'observateur précédent s'il existe
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Créer un nouvel observateur
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const buttonElement = document.getElementById('spacepubliccartridgeonlybuttonifsiblingout');
          if (buttonElement) {
            if (entry.isIntersecting) {
              buttonElement.classList.add('hidden');
            } else {
              buttonElement.classList.remove('hidden');
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observer l'élément cible
    if (targetRef.current) {
      observerRef.current.observe(targetRef.current);
    }
  }, [ua.isDesktop]);

  useEffect(() => {
    setupIntersectionObserver();
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [setupIntersectionObserver]);

  return { targetRef };
};

function Cartridge({
  calendar = [],
  spaceRef,
  hostRef,
  noRender,
  formules,
  capacities,
  dataByCategory,
}) {
  const [category] = useState('production');
  const { t } = useTranslation();
  const langCurrent = lang();
  const environment = useRelayEnvironment();

  // Récupération des données des stores
  const datesSelected = useSpacePublicZustandStore((state) => state.datesSelected);
  const riders = useSpacePublicZustandStore((state) => state.riders);
  const hostContactClick = useSpacePublicZustandStore((state) => state.hostContactClick);
  const { spaceId, publish } = useSpacePublicZustandStore.getState();
  const userData = useZusUserConnectedStore((state) => state.userData);
  const isAuthenticated = useZusUserConnectedStore((state) => state.isAuthenticated);
  const { ua } = useZusHomeStore.getState();

  // Utilisation des hooks personnalisés
  const { createBooking, gotoBookingUrl } = useCreateBooking(environment, spaceId, spaceRef, datesSelected, category, userData, isAuthenticated, langCurrent);

  const { step, setStep } = useStepManagement(isAuthenticated, userData, spaceRef, environment, langCurrent);

  const { targetRef } = useButtonVisibility(ua);

  // Effet pour gérer le clic sur le contact de l'hôte
  useEffect(() => {
    if (hostContactClick) {
      createBooking({ noCategory: true });
    }
  }, [hostContactClick, createBooking]);

  // Récupération du calendrier
  const spaceCalendar = useFragment(queryCalendar, calendar || null);

  // Si noRender est true, ne pas afficher le composant
  if (noRender) return null;

  // Rendu conditionnel en fonction de l'étape
  const renderContentStep = () => {
    if (step.value === STEPS.CHECKING_LAST_BOOKING) {
      return (
        <div className="relative flex items-center justify-center w-full py-8">
          <Loading />
        </div>
      );
    }

    if (userData && hostRef === userData.ref) {
      return (
        <div className="px-3 py-3 lg:px-6 lg:py-6">
          <span className="text-center text-dark">
            {t('booking:you_cant_book_your_space')}
          </span>
        </div>
      );
    }

    if (step.value === STEPS.STEP1) {
      return (
        <div className="px-3 py-3 pb-20 lg:pb-6 lg:px-6 lg:pt-6">
          <button
            className="w-full px-3 py-3 text-lg text-white transition bg-purple hover:bg-purple/80"
            onClick={() => setStep({ value: STEPS.STEP2 })}
          >
            {t('btn:new_request')}
          </button>
          <button
            className="w-full p-3 mt-3 text-lg text-center transition bg-purple-1 hover:bg-purple-1/80"
            onClick={() => {
              gotoBookingUrl(
                process.env.NEXT_PUBLIC_URL_V3
                  + get(step, 'spaceData.space.lastBookingByUser.frontURL'),
              );
            }}
          >
            {t('btn:use_booking')}
          </button>
        </div>
      );
    }

    return null;
  };

  const contentStep = renderContentStep();

  // Rendu du composant principal
  return (
    <div className="relative w-full lg:-mb-52 lg:-top-52 lg:w-3/7 lg:pl-4 xl:w-4/9 xl:pl-12">
      <div ref={targetRef}>
        <section id="catridge" className="relative z-50 bg-white rounded-xl lg:shadow-catridge lg:left-0 lg:bottom-14 lg:sticky lg:top-6 lg:mt-5">
          {contentStep || (
          <>
            <div className="block px-4 pt-4 pb-3 text-sm md:pt-5 md:pb-4 md:px-6">
              {!publish ? (
                <div className="flex flex-col items-center justify-center text-center min-h-[180px] text-purple-5/50 ">
                  <div>
                    <span className="block text-lg text-red ">
                      {t('space:no_published')}
                    </span>
                    <span className="text-base text-dark">
                      {t('space:no_booking')}
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <Formules formules={dataByCategory?.formules || []}/>
                  <CartridgeDates
                    {...{ spaceCalendar, dataByCategory }}
                    key="spacepubliccartridgedates"
                  />
                  <Riders {...{ capacities }} />
                  <Resume />
                </>
              )}
            </div>
            <div className="px-3 pb-16 text-sm leading-normal lg:px-6 lg:pb-6">
              <div className="relative z-5">
                <Button
                  className={`w-full py-3 text-lg lg:text-xl leading-normal md:rounded-b-lg flex items-center justify-center ${
                    !publish
                      ? 'bg-purple-0 text-dark-light'
                      : 'shadow-lg purple-gradient'
                  }`}
                  {...(!publish ? {} : { onClick: createBooking })}
                >
                  {isMobile ? t('btn:continue') : t('btn:ask_booking')}
                </Button>
              </div>
              {!publish ? null : (
                <div className="relative w-full px-1 pt-2 pb-2 leading-normal text-center -top-1 rounded-b-xl text-purple bg-purple/10">
                  {t('space:no_debit')}
                </div>
              )}
            </div>
          </>
          )}
        </section>
      </div>
      {!ua.isDesktop || (
      <div
        id="spacepubliccartridgeonlybuttonifsiblingout"
        className="hidden sticky md:min-w-[375px] lg:min-w-[375px] z-10 top-0"
      >
        <section className="bg-white md:shadow-catridge md:fixed lg:sticky rounded-b-xl">
            {contentStep || (
            <>
              <div className="p-6 text-sm">
                <div className="relative z-5">
                  <button
                    className={`w-full py-3 text-xl md:rounded-b-lg flex items-center justify-center ${
                      !publish
                        ? 'bg-purple-0 text-dark-light'
                        : 'shadow-lg blue'
                    }`}
                    {...(!publish ? {} : { onClick: createBooking })}
                  >
                    {t('btn:ask_booking')}
                  </button>
                </div>
                {!publish ? null : (
                <div className="relative w-full px-1 pt-2 pb-2 text-center -top-1 rounded-b-xl text-purple bg-purple/10">
                  {t('space:no_debit')}
                </div>
              )}
              </div>
            </>
            )}
        </section>
      </div>
      )}
    </div>
  );
}

function ComponentRoot(props) {
  const lazycontentdataloaded = useSpacePublicZustandStore((state) => state.lazycontentdataloaded);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (lazycontentdataloaded) {
      const { lazyCalendar } = useSpacePublicZustandStore.getState();
      setData({
        calendar: lazyCalendar,
      });
    }
  }, [lazycontentdataloaded]);

  if (!data) return null;
  return <Cartridge {...data} {...props} />;
}

export default ComponentRoot;
